import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Home from '@mui/icons-material/Home';
import { useLocaleHandler } from '../../hooks';
import { useTranslation } from 'react-i18next';
import {
  appBarSx,
  BurgerMenu,
  LanguageSelector,
  UserInfoChips,
  UserMenu,
  NavButton,
  SidebarToggle,
} from './header';

interface AdminHeaderProps {
  onDrawerToggle?: () => void;
  /** Сайдбар свёрнут (десктоп). Без обработчика кнопка не рисуется. */
  sidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
  /** Шапка сообщает наружу, что «Вернуться в кабинет» не влезла и её надо показать в меню. */
  onBackHiddenChange?: (hidden: boolean) => void;
}

const AdminHeader: React.FC<AdminHeaderProps> = ({
  onDrawerToggle,
  onBackHiddenChange,
  sidebarCollapsed = false,
  onToggleSidebar,
}) => {
  const { t } = useTranslation();
  const { currentLocale, changingLocale, handleLocaleChange } = useLocaleHandler();

  // «Вернуться в кабинет» убираем ПО ФАКТУ нехватки места, а не по брейкпоинту: ширина шапки
  // зависит не только от экрана, но и от содержимого — баланс монет бывает и 1 555, и 1 555 000,
  // а подписи локализованы. Тот же приём, что у AnimatedTabs и хлебных крошек вики.
  // Когда кнопка спрятана, её место — первым пунктом бургер-меню (см. AdminSidebar).
  const toolbarRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const [backHidden, setBackHidden] = useState(false);
  // Ширина, на которой кнопку спрятали: вернём, как только станет шире.
  const hiddenAtWidthRef = useRef(0);
  const [, setRemeasureTick] = useState(0);

  // Меряем на каждом рендере: содержимое шапки меняется без участия пропсов.
  // Решение принимается сравнением ширин, а не «поймали событие ресайза» — пропущенное
  // событие тогда лишь откладывает пересчёт, а не оставляет кнопку в неверном состоянии.
  useLayoutEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    const width = el.clientWidth;

    if (backHidden) {
      if (width > hiddenAtWidthRef.current) setBackHidden(false);
      return;
    }
    if (el.scrollWidth > width + 1) {
      hiddenAtWidthRef.current = width;
      setBackHidden(true);
    }
  });

  // Перемеряем и на смене ширины окна, и на смене содержимого правой группы.
  // Одного ResizeObserver на тулбаре мало: тулбар всегда во всю ширину, а сама шапка
  // не перерисовывается ни от поворота экрана, ни от смены баланса в чипе — эффект
  // без этого просто не запускался, и кнопка оставалась висеть в переполненной строке.
  useLayoutEffect(() => {
    const remeasure = () => setRemeasureTick((tick) => tick + 1);
    const observer = new ResizeObserver(remeasure);
    if (toolbarRef.current) observer.observe(toolbarRef.current);
    if (actionsRef.current) observer.observe(actionsRef.current);
    window.addEventListener('resize', remeasure);
    window.addEventListener('orientationchange', remeasure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', remeasure);
      window.removeEventListener('orientationchange', remeasure);
    };
  }, []);

  useEffect(() => {
    onBackHiddenChange?.(backHidden);
  }, [backHidden, onBackHiddenChange]);

  return (
    <AppBar position="fixed" elevation={0} sx={appBarSx}>
      <Toolbar ref={toolbarRef}>
        <BurgerMenu onDrawerToggle={onDrawerToggle} />
        <SidebarToggle collapsed={sidebarCollapsed} onToggle={onToggleSidebar} />

        {/* «Главная» — в центре шапки, как центральная навигация в кабинете и на публичных
            страницах (PersonalCabinetHeader): абсолютное позиционирование, чтобы кнопка стояла
            по центру ШАПКИ, а не по центру свободного места между бургером и правым блоком.
            Прячем там же, где появляется бургер (870px): на узкой шапке иконка домика без
            подписи только путала — навигация уходит в меню. */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'none',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            '@media (min-width: 870px)': { display: 'flex' },
          }}
        >
          <NavButton
            icon={<Home />}
            label={t('navigation:footer.home')}
            to="/"
            // Подпись — только от 1500: кнопка стоит по центру шапки и вне потока, поэтому
            // сама не расталкивает правый блок. С подписью (121px) на 1300 она наезжала на
            // «Вернуться в кабинет» на 24px; иконкой (40px) помещается с запасом.
            breakpoint={1500}
          />
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* Right side with Back to Dashboard and user info */}
        <Box ref={actionsRef} sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5, md: 2 } }}>
          {/* Back to Dashboard */}
          {!backHidden && (
            <NavButton
              icon={<ArrowBack />}
              label={t('admin:navigation.backToDashboard')}
              to="/dashboard"
              breakpoint={1300}
            />
          )}

          <UserInfoChips />

          <LanguageSelector
            currentLocale={currentLocale}
            changingLocale={changingLocale}
            onLocaleChange={handleLocaleChange}
          />

          <UserMenu />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default AdminHeader;
